import { ButtonHTMLAttributes, FC, forwardRef, PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';

import { useCommonStore } from '@/store';

import {
  ScButtonClose,
  ScButtonCloseProps,
  ScButtonDetails,
  ScButtonDetailsProps,
  ScButtonFullScreen,
  ScButtonFullScreenProps,
  ScButtonHeart,
  ScButtonHeartLoader,
  ScButtonHeartProps,
  ScButtonMore,
  ScButtonMoreProps,
  ScButtonTuple,
  ScButtonTupleProps,
  ScButtonWithArray,
  ScButtonWithArrayProps,
  ScScrollTopButton,
  ScScrollTopButtonProps,
  ScTabBarButton,
  ScTabBarButtonProps,
} from './styled';

export const ButtonClose: FC<
  ScButtonCloseProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => {
  const { crossIcon: CrossIcon, crossInCircleIcon: CrossInCircleIcon } =
    useTheme();

  return (
    <ScButtonClose {...props}>
      {props.inCircle ? <CrossInCircleIcon /> : <CrossIcon />}
    </ScButtonClose>
  );
};

export const ButtonMore: FC<
  ScButtonMoreProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => (
  <ScButtonMore {...props}>
    <div />
    <div />
    <div />
  </ScButtonMore>
);

export const ButtonDetails: FC<
  ScButtonDetailsProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => (
  <ScButtonDetails {...props}>
    <div />
    <div />
    <div />
  </ScButtonDetails>
);

export const ButtonHeart: FC<
  ScButtonHeartProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => <ScButtonHeart {...props} />;

export const ButtonHeartLoader: FC<
  ScButtonHeartProps & ButtonHTMLAttributes<HTMLDivElement>
> = (props) => <ScButtonHeartLoader {...props} />;

export const ButtonFullScreen: FC<
  ScButtonFullScreenProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => <ScButtonFullScreen {...props} />;

export const ButtonWithArray = forwardRef<
  HTMLButtonElement,
  ScButtonWithArrayProps &
    PropsWithChildren &
    ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, ...props }, ref) => {
  const { icon: Icon } = props;
  const { images } = useTheme();

  return (
    <ScButtonWithArray ref={ref} {...props}>
      <div className="value">{children}</div>
      <div className="arrow">
        <images.dropdownArrow.component />
      </div>
      {Icon != null && (
        <div className="icon">
          <Icon />
        </div>
      )}
    </ScButtonWithArray>
  );
});

export const ButtonProfile = forwardRef<
  HTMLButtonElement,
  ScButtonTupleProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const { profileIcon: ProfileIcon, crossIcon: CrossIcon } = useTheme();

  return (
    <ScButtonTuple ref={ref} {...props}>
      <div className="tuple">
        <div className="tuple_item">
          <ProfileIcon />
        </div>
        <div className="tuple_item">
          <CrossIcon />
        </div>
      </div>
    </ScButtonTuple>
  );
});

export const ButtonSupport = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const { supportIcon: SupportIcon } = useTheme();

  return (
    <ScTabBarButton ref={ref} {...props} aria-label="support">
      <SupportIcon />
    </ScTabBarButton>
  );
});

export const ButtonSearch = forwardRef<
  HTMLButtonElement,
  ScButtonTupleProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const { magnifierLargeIcon: MagnifierLargeIcon, closeIcon: CrossIcon } =
    useTheme();

  return (
    <ScButtonTuple ref={ref} {...props} aria-label="search">
      <div className="tuple">
        <div className="tuple_item">
          <MagnifierLargeIcon />
        </div>
        <div className="tuple_item">
          <CrossIcon />
        </div>
      </div>
    </ScButtonTuple>
  );
});

export const ButtonPromo = forwardRef<
  HTMLButtonElement,
  ScTabBarButtonProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const { presentIcon: PresentIcon } = useTheme();

  return (
    <ScTabBarButton ref={ref} {...props} aria-label="promo">
      <PresentIcon />
    </ScTabBarButton>
  );
});

export const ButtonBurger = forwardRef<
  HTMLButtonElement,
  ScButtonTupleProps & ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => {
  const { menuIcon: MenuIcon, closeIcon: CrossIcon } = useTheme();

  return (
    <ScButtonTuple ref={ref} {...props} aria-label="menu">
      <div className="tuple">
        <div className="tuple_item">
          <MenuIcon />
        </div>
        <div className="tuple_item">
          <CrossIcon />
        </div>
      </div>
    </ScButtonTuple>
  );
});

export const ScrollTopButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> &
    ScScrollTopButtonProps & { isTop: boolean }
>(({ isTop, ...props }, ref) => {
  const handleScrollTop = () =>
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });

  const tabBarHeight = useCommonStore.use.tabBarHeight();

  return !isTop ? (
    <ScScrollTopButton
      ref={ref}
      onClick={handleScrollTop}
      bottom={tabBarHeight}
      {...props}
    >
      <div className="arrow" />
    </ScScrollTopButton>
  ) : null;
});

export const ButtonHome = forwardRef<
  HTMLButtonElement,
  ScTabBarButtonProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const { casinoIcon: CasinoIcon } = useTheme();

  return (
    <ScTabBarButton ref={ref} {...props}>
      <CasinoIcon />
    </ScTabBarButton>
  );
});

export const ButtonSports = forwardRef<
  HTMLButtonElement,
  ScTabBarButtonProps & ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => {
  const { sportsIcon: SportsIcon } = useTheme();

  return (
    <ScTabBarButton ref={ref} {...props}>
      <SportsIcon />
    </ScTabBarButton>
  );
});

export { Button } from './Button';
